import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, FileSpreadsheet, Plus } from "lucide-react";
import { toast } from "sonner";
import { download } from "@/lib/download";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatIsoDay, formatMoney, formatNumber, formatPct, formatTRY } from "@/lib/format";
import { ApprovalFlag, PAYMENTS, STATUS, StatusBadge, StockFlag, printSummary } from "@/lib/orders";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import { TestBadge } from "@/components/common/TestBadge";

const PAGE_SIZE = 25;
const sel = "h-9 px-2 rounded-md border border-slate-200 bg-white text-sm";

function Filters({ f, setF, reps }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="flex flex-wrap gap-2 p-3 border-b border-slate-200" data-testid="orders-filters">
      <Input className="h-9 w-56" placeholder="Sipariş no, cari..." value={f.q} onChange={set("q")} data-testid="orders-search-input" />
      <Input type="date" className="h-9 w-40" value={f.date_from} onChange={set("date_from")} data-testid="orders-date-from" />
      <Input type="date" className="h-9 w-40" value={f.date_to} onChange={set("date_to")} data-testid="orders-date-to" />
      <select className={sel} value={f.status} onChange={set("status")} data-testid="orders-status-select">
        <option value="">Tüm durumlar</option>{Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select className={sel} value={f.payment_method} onChange={set("payment_method")} data-testid="orders-payment-select">
        <option value="">Tüm ödemeler</option>{Object.entries(PAYMENTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {reps.length > 0 && (
        <select className={sel} value={f.sales_rep_id} onChange={set("sales_rep_id")} data-testid="orders-rep-select">
          <option value="">Tüm temsilciler</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      )}
    </div>
  );
}

export default function Orders() {
  const { can, flag } = useAuth();
  const canSell = can("hizli_satis", "create") || can("siparisler", "create");
  const navigate = useNavigate();
  useEffect(() => {
    if (!canSell) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (e.key.toLowerCase() !== "n" || e.ctrlKey || e.metaKey || e.altKey || t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      if (document.querySelector("[role='dialog']")) return;
      e.preventDefault();
      navigate("/hizli-satis");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSell, navigate]);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const [f, setF] = useState({ q: "", date_from: "", date_to: "", status: params.get("status") || "", payment_method: "", sales_rep_id: "", page: 1 });
  const [reps, setReps] = useState([]);
  const [res, setRes] = useState(null);
  const [invSort, setInvSort] = useState("-invoice_no");
  useEffect(() => { if (can("cariler")) api.get("/customers/sales-reps").then(({ data }) => setReps(data)); }, [can]);
  useEffect(() => {
    setRes(null);
    const extra = tab === "pending" ? { status: "onay_bekliyor" } : tab === "invoices" ? { status: "faturalasti", sort: invSort } : {};
    const p = { ...f, page_size: PAGE_SIZE, ...extra };
    const t = setTimeout(() => api.get("/orders", { params: p }).then(({ data }) => setRes(data)), 250);
    return () => clearTimeout(t);
  }, [f, tab, invSort]);
  const invoices = tab === "invoices";
  const toggleSort = () => { setInvSort(invSort === "-invoice_no" ? "invoice_no" : "-invoice_no"); setF({ ...f, page: 1 }); };
  const showProfit = flag("profit_view");
  const cols = 9 + (showProfit ? 1 : 0) + (invoices ? 1 : 0);
  const exportXlsx = () => { const extra = tab === "pending" ? { status: "onay_bekliyor" } : tab === "invoices" ? { status: "faturalasti", sort: invSort } : {}; const { page, ...rest } = f;
    download("/orders/export", { ...rest, ...extra }, "satislar.xlsx").catch((e) => toast.error(apiError(e))); };
  return (
    <div data-testid="orders-page">
      <PageHeader title="Satışlar" subtitle={`Satış belgeleri (proforma / fatura) ve durum takibi${res?.scope === "own" ? " · yalnızca kendi satışlarınız" : ""}`} testid="orders-title">
        <Button variant="outline" onClick={exportXlsx} title="Filtrelenmiş listenin tamamı (tüm sayfalar)" data-testid="orders-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        {canSell && <Button onClick={() => navigate("/hizli-satis")} className="bg-blue-600 hover:bg-blue-700 shadow-sm" title="Kısayol: N" data-testid="orders-new-sale-button">
          <Plus className="h-4 w-4 mr-1" />Yeni Satış<kbd className="ml-2 text-[10px] font-mono bg-blue-500/60 rounded px-1">N</kbd></Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={(v) => { setParams(v === "all" ? {} : { tab: v }); setF({ ...f, page: 1, status: "" }); }} className="mb-3">
        <TabsList>
          <TabsTrigger value="all" data-testid="orders-tab-all">Tüm Satışlar</TabsTrigger>
          {can("siparisler", "approve") && <TabsTrigger value="pending" data-testid="orders-tab-pending">Onay Bekleyenler</TabsTrigger>}
          <TabsTrigger value="invoices" data-testid="orders-tab-invoices">Faturalar</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="panel overflow-hidden">
        <Filters f={f} setF={setF} reps={reps} />
        <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
          <table className="erp-table" data-testid="orders-table">
            <thead><tr>{invoices && (
              <th className="cursor-pointer select-none" onClick={toggleSort} data-testid="orders-invoice-sort">
                Fatura No {invSort === "invoice_no" ? <ArrowUp className="inline h-3 w-3" /> : <ArrowDown className="inline h-3 w-3" />}
              </th>)}<th>Sipariş No</th><th>Tarih</th><th>Cari</th><th className="text-right">Tutar</th><th className="text-right">TL</th>{showProfit && <th className="text-right">Marj</th>}<th>Baskı</th><th>Durum</th><th title="Bekleyen adet × net birim fiyat (iskonto sonrası, KDV hariç)">Bekleyen</th></tr></thead>
            <tbody>
              {!res ? <LoadingRow cols={cols} /> : res.items.length === 0 ? <EmptyRow cols={cols} /> : res.items.map((o) => (
                <tr key={o.id} className="cursor-pointer" onClick={() => navigate(`/siparisler/${o.id}`)} data-testid={`order-row-${o.order_no}`}>
                  {invoices && <td className="font-mono text-xs font-semibold text-emerald-700" data-testid={`invoice-row-no-${o.invoice_no}`}>{o.invoice_no}<div className="text-[10px] font-normal text-slate-400">{formatIsoDay(o.invoice_date)}</div></td>}
                  <td className="font-mono text-xs font-semibold text-slate-900">{o.order_no}<TestBadge show={o.is_test} testid={`order-test-badge-${o.order_no}`} />{!invoices && o.invoice_no && <div className="text-[10px] font-normal text-emerald-700" data-testid={`order-invoice-no-${o.order_no}`}>{o.invoice_no}</div>}</td>
                  <td className="font-mono text-xs">{formatIsoDay(o.order_date)}</td>
                  <td className="max-w-[240px] truncate">{o.customer_title}</td>
                  <td className="text-right font-mono">{formatMoney(o.grand_total, o.currency)}</td>
                  <td className="text-right font-mono font-semibold">{formatTRY(o.grand_total_try)}</td>
                  {showProfit && <td className={`text-right font-mono ${o.margin_pct < 15 ? "text-rose-600" : "text-emerald-700"}`}>{formatPct(o.margin_pct)}</td>}
                  <td className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`order-prints-${o.order_no}`}>{printSummary(o.print_counts)}</td>
                  <td className="whitespace-nowrap"><StatusBadge status={o.display_status || o.status} label={o.status_label} testid={`order-status-${o.order_no}`} /><ApprovalFlag o={o} /><StockFlag o={o} /></td>
                  <td className="whitespace-nowrap text-[12px]" data-testid={`order-backorder-${o.order_no}`}>{o.backorder_pending_qty > 0 ? (
                    <button className="rounded bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-amber-800 hover:bg-amber-100" title="Bekleyen adet × net birim fiyat (iskonto sonrası, KDV hariç)" onClick={(e) => { e.stopPropagation(); navigate(`/cariler/${o.customer_id}?tab=bekleyen&order=${o.id}`); }} data-testid={`order-backorder-link-${o.order_no}`}>
                      Bekleyen: {formatNumber(o.backorder_pending_qty)} adet ({formatNumber(o.backorder_pending_koli)} koli) · {formatTRY(o.backorder_pending_value)}</button>) : <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {res && <Pager page={f.page} pageSize={PAGE_SIZE} total={res.total} onPage={(p) => setF({ ...f, page: p })} testid="orders-pager" />}
      </div>
    </div>
  );
}
